import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"

import { appError, setLiveAppCompleted } from '../../../../../redux/slices/app'
import api from '../../../../../utils/api'
import { AppDepositAttribution, InitPaymentParams, PaymentFlowSessionAction } from "../../../../../utils/types"
import Utils from '../../../../../utils/utils'
import { captureAdAttribution } from '../../../../../utils/attribution'
import { useCaptcha } from '../../../../Presentational/CaptchaModal'
import { submitPostForm } from './utils'
import useDepositResultPage from "./useDepositResultPage"
import { useAppState } from "../../../../../utils/hooks"
import { useDashboardAnalytics } from "../../../Onboarding/useDashboardAnalytics"
import { mapPaymentMethod, normalizeProcessorName } from "../../../../../utils/floodlightEvents"
import { gclidParam } from "../../../../../utils/googleClickId"
import { getSubmitDepositType } from "./depositType"

export interface ProcessorLikeObject {
  id: number | string
  name: string
  type?: string | null
  displayName?: string | null
}

export interface AccountLikeObject {
  account: {
    id: number
  }
}

export const useInitiatePayment = (processor: ProcessorLikeObject | undefined, account: AccountLikeObject | undefined, submitForm: () => void) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { redirectedFromWTR, liveAccounts } = useAppState()
  const { captcha, showCaptcha, captchaModal } = useCaptcha(submitForm)
  const getResultPage = useDepositResultPage()
  const { fireDeposit } = useDashboardAnalytics()

  const initiatePayment = async (options: {
    amount: string,
    currencyId: string | number,
    additionalParamOptions?: Partial<InitPaymentParams['options']>,
    onIframeOpen?: (redirectUrl: string) => void,
    // THPRT-6277: Checkout.com Flow returns a Payment Session instead of a
    // post/redirect. The caller mounts the Flow drop-in with this action
    // (publicKey + paymentSession come from the resolved BE SubProcessor record).
    // transactionId is our tfx id — the result page resolves the deposit by it
    // (NOT the Checkout.com pay_… id from the drop-in's onPaymentCompleted).
    onFlowSession?: (action: PaymentFlowSessionAction, transactionId: string) => void,
    ignoreResultAction?: boolean,
    // THPRT-6558 / AFS-2.1: app attribution read from the /checkoutcom URL params,
    // forwarded to the BE so an in-app web-view deposit records the app's sourcePortal.
    appDeposit?: AppDepositAttribution
  }) => {
    try {
      if (!processor || !account) {
        return
      }

      const matchedAccount = liveAccounts.find(a => a.account.id === account.account.id)

      const appDeposit = options.appDeposit
      let data: InitPaymentParams = {
        accountId: account.account.id,
        currencyId: options.currencyId,
        amount: options.amount,
        processorId: processor.id,
        // THPRT-6558 / AFS-2.1: use the app's sourcePortal when supplied, else keep
        // the browser default (AFS-2.2). The five fields are forwarded only when present.
        sourcePortal: appDeposit?.sourcePortal || 'C_PORTAL',
        ...gclidParam(),
        resultPage: getResultPage(undefined, redirectedFromWTR),
        options: { ...options.additionalParamOptions },
        ...(appDeposit?.appsflyerId && { appsflyerId: appDeposit.appsflyerId }),
        ...(appDeposit?.osVersion && { osVersion: appDeposit.osVersion }),
        ...(appDeposit?.appVersion && { appVersion: appDeposit.appVersion }),
        ...(appDeposit?.att !== undefined && { att: appDeposit.att }),
        ...(appDeposit?.advertisingId && { advertisingId: appDeposit.advertisingId })
      }

      const response = await api.initiatePayment(data, captcha)
      if (Utils.validResponse(response)) {
        const result = response.payload[0].result
        if (matchedAccount) {
          // THPRT-6478: capture ad attribution as a live deposit begins, so the
          // signals are on record before Purchase even for clients not captured
          // earlier. Backend is first-write-wins, so it no-ops if already stored.
          captureAdAttribution()
          const depositType = await getSubmitDepositType()
          fireDeposit('submitted', {
            amount: options.amount,
            currency: matchedAccount.account.currency.code,
            paymentMethod: mapPaymentMethod(processor.type),
            processor: normalizeProcessorName(processor.displayName ?? processor.name),
            transactionId: (result.payment?.id ?? result.transactionId) || result.payment?.transactionId,
            depositStatus: response.payload[0].status,
            depositType,
            accountId: matchedAccount.account.accountNumber,
            accountType: matchedAccount.account.accountTradingType?.accountTradingType,
            productTier: matchedAccount.platformAccountType,
            platform: matchedAccount.account.platform?.name,
          })
        }
        if (result.action?.type) {
          switch (result.action.type) {
            case 'nop':
              if (options.ignoreResultAction) {
                break
              }

              history.push(getResultPage(result.transactionId))
              break
            case 'post':
              const acceptCharset = processor.name === 'Quick Payment' ? 'Shift-JIS' : 'UTF-8'
              if (redirectedFromWTR) {
                Utils.postMessageToWTR({
                  message: 'depositRedirect',
                  acceptCharset: acceptCharset,
                  action: result.action
                })
              } else {
                const resultFields = processor.name === 'CheckoutCom' ? {} : result.action.fields
                const resultURL = processor.name === 'CheckoutCom' ? result.action.url.replace(/"/g, '') : result.action.url
                submitPostForm(result.action.type, resultURL, resultFields, acceptCharset)
              }
              break
            case 'redirect':
              if (redirectedFromWTR) {
                Utils.postMessageToWTR({
                  message: 'depositRedirect',
                  action: result.action
                })
              } else {
                window.open(result.action.url, '_self')
              }
              break
            case 'iframe':
              options.onIframeOpen && options.onIframeOpen(result.action.url)
              break
            case 'checkout_flow':
              // THPRT-6277: hand the Checkout.com Flow action (publicKey +
              // paymentSession) to the caller (CheckoutFlow) to mount the drop-in.
              // Outcome arrives via the drop-in's onPaymentCompleted plus the BE webhook.
              // THPRT-6317: the result page resolves the deposit by the tfx transaction
              // id. Read it resiliently — fall back to the nested payment transaction id
              // when the top-level field is empty/absent — so a successful deposit is
              // never routed to the result page without an id.
              options.onFlowSession && options.onFlowSession(
                result.action,
                result.transactionId || result.payment?.transactionId || ''
              )
              break
          }
        }
      }
      const { status } = response.payload[0]
      if (status === 'CHALLENGE') {
        showCaptcha()
      } else if (!Utils.validResponse(response)) {
        if (matchedAccount) {
          fireDeposit('failed', {
            amount: options.amount,
            currency: matchedAccount.account.currency.code,
            paymentMethod: mapPaymentMethod(processor.type),
            processor: normalizeProcessorName(processor.displayName ?? processor.name),
            depositStatus: status,
            accountId: matchedAccount.account.accountNumber,
            accountType: matchedAccount.account.accountTradingType?.accountTradingType,
            productTier: matchedAccount.platformAccountType,
            platform: matchedAccount.account.platform?.name,
          })
        }
        dispatch(appError(Utils.statusError(response)))
        return status
      }

    } catch (e: any) {
      const ma = account && liveAccounts.find(a => a.account.id === account.account.id)
      if (ma && processor) {
        fireDeposit('incomplete', {
          amount: options.amount,
          currency: ma.account.currency.code,
          paymentMethod: mapPaymentMethod(processor.type),
          processor: normalizeProcessorName(processor.displayName ?? processor.name),
          accountId: ma.account.accountNumber,
          accountType: ma.account.accountTradingType?.accountTradingType,
          productTier: ma.platformAccountType,
          platform: ma.account.platform?.name,
        })
      }
      dispatch(appError(Utils.customError(e?.message)))
    }
    finally { dispatch(setLiveAppCompleted(true)) }
  }

  return { initiatePayment, captchaModal }
}
